/**
 * Client-side PDF rendering helpers backed by pdf.js.
 *
 * Chat attachments are delivered from Cloudinary under `/raw/upload/`, which
 * does not support server-side transformations — so first-page thumbnails have
 * to be rasterised in the browser. pdf.js is heavy, so it is imported lazily on
 * first use and the resulting module is cached for the session.
 */

import type { PDFDocumentProxy } from 'pdfjs-dist';

type PdfJsModule = typeof import('pdfjs-dist');

let pdfjsPromise: Promise<PdfJsModule> | null = null;

/** Load pdf.js on demand and point it at a bundled worker. */
export function loadPdfJs(): Promise<PdfJsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

// Documents are cached per URL so the bubble thumbnail and the fullscreen
// reader don't download and parse the same file twice.
const documentCache = new Map<string, Promise<PDFDocumentProxy>>();

export function loadPdfDocument(url: string): Promise<PDFDocumentProxy> {
  const cached = documentCache.get(url);
  if (cached) return cached;

  const task = loadPdfJs().then(
    (pdfjs) =>
      pdfjs.getDocument({
        url,
        // Cloudinary serves these cross-origin; no credentials required.
        withCredentials: false,
      }).promise,
  );

  // Don't cache failures — a transient network error shouldn't poison the URL.
  task.catch(() => documentCache.delete(url));
  documentCache.set(url, task);
  return task;
}

export interface RenderPageOptions {
  /**
   * Write explicit CSS width/height onto the canvas. The reader wants this so
   * pages sit at an exact zoom size; the bubble thumbnail does not, because it
   * crops the page to a fixed-height frame via CSS instead.
   */
  applyStyleSize?: boolean;
  /** Receives the in-flight render task so callers can cancel on unmount. */
  onTask?: (task: { cancel: () => void }) => void;
}

/**
 * Render a single page into a canvas, scaled to fit `targetWidth` CSS pixels
 * and sharpened for the current device pixel ratio.
 *
 * The render task is handed back through `onTask` — React runs effects twice in
 * StrictMode, and two concurrent renders against one canvas will deadlock in
 * pdf.js, so callers must cancel the previous task on cleanup.
 */
export async function renderPdfPage(
  doc: PDFDocumentProxy,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  targetWidth: number,
  options: RenderPageOptions = {},
): Promise<{ width: number; height: number }> {
  const { applyStyleSize = true, onTask } = options;
  const page = await doc.getPage(pageNumber);

  const baseViewport = page.getViewport({ scale: 1 });
  const scale = targetWidth / baseViewport.width;
  const viewport = page.getViewport({ scale });

  // Cap DPR so very large pages on retina screens don't blow up memory.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  canvas.width = Math.floor(viewport.width * dpr);
  canvas.height = Math.floor(viewport.height * dpr);
  if (applyStyleSize) {
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
  }

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable');

  const task = page.render({
    canvas,
    canvasContext: context,
    viewport,
    transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
  });

  onTask?.(task);
  await task.promise;

  return { width: viewport.width, height: viewport.height };
}

/** True when the file can be rasterised by pdf.js. */
export function isPdfAttachment(fileName: string, mimeType?: string): boolean {
  if (mimeType === 'application/pdf') return true;
  return /\.pdf$/i.test(fileName.split('?')[0] || '');
}

/**
 * Why a document failed to load. `blocked` is the common one in this app:
 * Cloudinary returns 401 "deny or ACL failure" for PDFs unless PDF delivery is
 * enabled on the account, so the file is unreachable rather than broken.
 */
export type PdfErrorKind = 'blocked' | 'missing' | 'invalid' | 'network';

export function classifyPdfError(err: unknown): PdfErrorKind {
  const e = err as { name?: string; status?: number; message?: string } | undefined;

  const status = typeof e?.status === 'number' ? e.status : undefined;
  if (status === 401 || status === 403) return 'blocked';
  if (status === 404 || e?.name === 'MissingPDFException') return 'missing';
  if (e?.name === 'InvalidPDFException' || e?.name === 'PasswordException') return 'invalid';

  // pdf.js surfaces the status in the message when the exception type is generic.
  const message = e?.message || '';
  if (/\b(401|403)\b/.test(message)) return 'blocked';
  if (/\b404\b/.test(message)) return 'missing';

  return 'network';
}

/** User-facing copy for a load failure. */
export function describePdfError(kind: PdfErrorKind, noun = 'file'): string {
  switch (kind) {
    case 'blocked':
      return `This ${noun} can't be opened — the server refused access to it (HTTP 401).`;
    case 'missing':
      return `This ${noun} is no longer available on the server.`;
    case 'invalid':
      return `This ${noun} is damaged or password-protected, so it can't be previewed.`;
    default:
      return `Couldn't load this ${noun}. Check your connection and try again.`;
  }
}
